import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Clock, Gamepad, Lock, Sparkles } from 'lucide-react';

export default function PurchaseTimerWidget() {
    const { publicSettings } = useSelector((s) => s.gateway || {});
    const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
    const [isFuture, setIsFuture] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [isDraggingPivot, setIsDraggingPivot] = useState(false);

    // Coords relative to container: x/y for card center, xp for horizontal sliding pivot
    const [coords, setCoords] = useState(() => {
        if (typeof window === 'undefined') return { x: 80, y: 70, xp: 0, angle: 30 };
        const savedXp = parseFloat(localStorage.getItem('purchase_timer_xp'));
        const initialXp = isNaN(savedXp) ? 0 : Math.max(-80, Math.min(80, savedXp));
        return { x: 80 + initialXp, y: 70, xp: initialXp, angle: 30 };
    });

    const containerRef = useRef(null);
    const isDraggingRef = useRef(false);
    const isDraggingPivotRef = useRef(false);
    
    // Initialize refs with saved pivot displacement if available
    const getSavedXp = () => {
        if (typeof window === 'undefined') return 0;
        const savedXp = parseFloat(localStorage.getItem('purchase_timer_xp'));
        return isNaN(savedXp) ? 0 : Math.max(-80, Math.min(80, savedXp));
    };
    const initialXp = getSavedXp();
    
    const xRef = useRef(80 + initialXp);   // Horizontal displacement from center
    const yRef = useRef(70);   // Vertical displacement from center
    const xpRef = useRef(initialXp);   // Horizontal pivot displacement from center
    const vxRef = useRef(0);   // Card velocity X
    const vyRef = useRef(0);   // Card velocity Y
    const angleRef = useRef(30);
    const lastTimeRef = useRef(0);
    const grabOffsetRef = useRef({ x: 0, y: 0 });

    useEffect(() => {
        if (!publicSettings?.purchasePaused || !publicSettings?.purchaseStartsAt) {
            setIsFuture(false);
            return;
        }

        const targetTime = new Date(publicSettings.purchaseStartsAt).getTime();

        const calculateTime = () => {
            const now = Date.now();
            const difference = targetTime - now;
            if (difference <= 0) {
                setIsFuture(false);
                return;
            }

            setIsFuture(true);
            setTimeLeft({
                days: Math.floor(difference / (1000 * 60 * 60 * 24)),
                hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
                minutes: Math.floor((difference / 1000 / 60) % 60),
                seconds: Math.floor((difference / 1000) % 60)
            });
        };

        calculateTime();
        const interval = setInterval(calculateTime, 1000);
        return () => clearInterval(interval);
    }, [publicSettings]);

    useEffect(() => {
        let animationFrameId;
        let lastTimestamp = performance.now();

        const updatePhysics = (timestamp) => {
            let dt = (timestamp - lastTimestamp) / 1000;
            if (dt > 0.1) dt = 0.1; // Cap dt to avoid issues on tab background
            lastTimestamp = timestamp;

            const x = xRef.current;
            const y = yRef.current;
            const xp = xpRef.current;

            // Vector from pivot (xp, 0) to card (x, y)
            const dx = x - xp;
            const dy = y;

            const d = Math.sqrt(dx * dx + dy * dy) || 0.1;
            const L = 112; // Natural rope length

            // Spring constant (stiffness of rope) and damping
            const kSpring = 160.0;
            const damping = 1.6;
            const gravity = 850.0;

            // Tension force acts along the rope towards pivot
            const tensionMagnitude = -kSpring * (d - L);
            const tx = tensionMagnitude * (dx / d);
            const ty = tensionMagnitude * (dy / d);

            if (!isDraggingRef.current) {
                // Card physics: Tension + Gravity + Damping
                const fx = tx - damping * vxRef.current;
                const fy = ty + gravity - damping * vyRef.current;

                // Add slight ambient wind force when resting
                const velocityMagnitude = Math.sqrt(vxRef.current * vxRef.current + vyRef.current * vyRef.current);
                if (velocityMagnitude < 2.0 && Math.abs(d - L) < 1.0) {
                    const wind = 5.0 * Math.sin(timestamp / 700);
                    vxRef.current += wind * dt;
                }

                vxRef.current += fx * dt;
                vyRef.current += fy * dt;

                xRef.current += vxRef.current * dt;
                yRef.current += vyRef.current * dt;
                angleRef.current = Math.atan2(dx, dy) * (180 / Math.PI);
            }

            setCoords({
                x: xRef.current,
                y: yRef.current,
                xp: xpRef.current,
                angle: angleRef.current
            });

            animationFrameId = requestAnimationFrame(updatePhysics);
        };

        animationFrameId = requestAnimationFrame(updatePhysics);

        return () => {
            cancelAnimationFrame(animationFrameId);
        };
    }, []);

    if (!publicSettings?.purchasePaused) {
        return null;
    }

    const padZero = (num) => String(num).padStart(2, '0');

    const getContainerRelativeCoords = (clientX, clientY) => {
        if (!containerRef.current) return { x: 0, y: 0 };
        const rect = containerRef.current.getBoundingClientRect();
        
        // Account for any CSS scale transformation when calculating relative coordinate bounds
        const scale = rect.width / 240; 
        return {
            x: (clientX - rect.left) / scale - 120, // Center is at 120px relative offset
            y: (clientY - rect.top) / scale
        };
    };

    // Card drag handlers
    const handlePointerDown = (e) => {
        isDraggingRef.current = true;
        setIsDragging(true);

        const p = getContainerRelativeCoords(e.clientX, e.clientY);
        grabOffsetRef.current = {
            x: p.x - xRef.current,
            y: p.y - yRef.current
        };

        vxRef.current = 0;
        vyRef.current = 0;
        lastTimeRef.current = performance.now();

        e.currentTarget.setPointerCapture(e.pointerId);
    };

    const handlePointerMove = (e) => {
        if (!isDraggingRef.current) return;

        const p = getContainerRelativeCoords(e.clientX, e.clientY);
        const targetX = p.x - grabOffsetRef.current.x;
        const targetY = Math.max(20, p.y - grabOffsetRef.current.y); // Prevent dragging above pivot

        const now = performance.now();
        const dt = (now - lastTimeRef.current) / 1000;
        if (dt > 0) {
            vxRef.current = (targetX - xRef.current) / dt;
            vyRef.current = (targetY - yRef.current) / dt;
        }
        lastTimeRef.current = now;

        xRef.current = targetX;
        yRef.current = targetY;

        // Calculate angle from the dynamic sliding pivot
        const dx = targetX - xpRef.current;
        const dy = targetY;
        angleRef.current = Math.atan2(dx, dy) * (180 / Math.PI);
    };

    const handlePointerUp = (e) => {
        if (!isDraggingRef.current) return;
        isDraggingRef.current = false;
        setIsDragging(false);
        e.currentTarget.releasePointerCapture(e.pointerId);
    };

    const handlePointerEnter = (e) => {
        if (isDraggingRef.current || isDraggingPivotRef.current) return;

        // Give it a realistic physical kick on cursor sweep
        const p = getContainerRelativeCoords(e.clientX, e.clientY);
        const kickDir = p.x < xRef.current ? 160 : -160;
        vxRef.current += kickDir;
    };

    // Pivot drag handlers
    const handlePivotDown = (e) => {
        isDraggingPivotRef.current = true;
        setIsDraggingPivot(true);

        const p = getContainerRelativeCoords(e.clientX, e.clientY);
        grabOffsetRef.current = {
            x: p.x - xpRef.current,
            y: 0
        };

        e.currentTarget.setPointerCapture(e.pointerId);
        e.stopPropagation();
    };

    const handlePivotMove = (e) => {
        if (!isDraggingPivotRef.current) return;

        const p = getContainerRelativeCoords(e.clientX, e.clientY);
        const targetX = p.x - grabOffsetRef.current.x;

        xpRef.current = Math.max(-80, Math.min(80, targetX));

        // Recalculate card angle based on new pivot X position
        const dx = xRef.current - xpRef.current;
        const dy = yRef.current;
        angleRef.current = Math.atan2(dx, dy) * (180 / Math.PI);
        e.stopPropagation();
    };

    const handlePivotUp = (e) => {
        if (!isDraggingPivotRef.current) return;
        isDraggingPivotRef.current = false;
        setIsDraggingPivot(false);
        e.currentTarget.releasePointerCapture(e.pointerId);

        // Save the drop point
        localStorage.setItem('purchase_timer_xp', xpRef.current.toString());
        e.stopPropagation();
    };

    return (
        <div
            ref={containerRef}
            className="fixed top-0 -right-4 sm:-right-3 md:right-10 z-[45] select-none pointer-events-none origin-top scale-[0.75] md:scale-100"
            style={{ width: '240px', height: '380px' }}
        >
            {/* SVG Wire/Cord that stretches and rotates dynamically */}
            <svg className="absolute top-0 left-0 w-full h-full pointer-events-none z-20 overflow-visible">
                {/* Horizontal sliding bar guide at the top */}
                <line
                    x1="40"
                    y1="0"
                    x2="200"
                    y2="0"
                    stroke="#27272a"
                    strokeWidth="2"
                    strokeLinecap="round"
                    style={{ opacity: 0.3 }}
                />

                {/* Connection screw at the top sliding pivot point */}
                {/* <circle
                    cx={120 + coords.xp}
                    cy="0"
                    r="4.5"
                    fill="#52525b"
                    stroke="#a1a1aa"
                    strokeWidth="1.5"
                    style={{ filter: 'drop-shadow(0px 1px 1px rgba(0,0,0,0.6))' }}
                /> */}

                {/* Layered solid lines for 100% reliable metallic/gradient visual look */}
                {/* Dark outline/shadow */}
                <line
                    x1={120 + coords.xp}
                    y1="0"
                    x2={120 + coords.x}
                    y2={coords.y}
                    stroke="#18181b"
                    strokeWidth="3.5"
                    strokeLinecap="round"
                />
                {/* Metallic core */}
                <line
                    x1={120 + coords.xp}
                    y1="0"
                    x2={120 + coords.x}
                    y2={coords.y}
                    stroke="#a1a1aa"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                />

                {/* Dynamic ring connector at the bottom of the wire */}
                <circle
                    cx={120 + coords.x}
                    cy={coords.y}
                    r="5"
                    fill="transparent"
                    stroke="#a1a1aa"
                    strokeWidth="1.5"
                    style={{ filter: 'drop-shadow(1px 2px 2px rgba(0,0,0,0.5))' }}
                />
            </svg>

            {/* Draggable peg hit target (HTML overlay for 100% reliable touch/mouse interaction) */}
            <div
                onPointerDown={handlePivotDown}
                onPointerMove={handlePivotMove}
                onPointerUp={handlePivotUp}
                onPointerCancel={handlePivotUp}
                style={{
                    position: 'absolute',
                    left: `${120 + coords.xp}px`,
                    top: '-15px',
                    width: '40px',
                    height: '40px',
                    transform: 'translate(-50%, 0)',
                    cursor: isDraggingPivot ? 'grabbing' : 'grab',
                    pointerEvents: 'auto',
                    zIndex: 30,
                    touchAction: 'none',
                    borderRadius: '50%'
                }}
            />

            {/* Draggable Card Face */}
            <div
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
                onPointerEnter={handlePointerEnter}
                style={{
                    position: 'absolute',
                    left: `${120 + coords.x}px`,
                    top: `${coords.y}px`,
                    transform: `translate(-50%, 0) rotate(${coords.angle}deg)`,
                    touchAction: 'none',
                    cursor: isDragging ? 'grabbing' : 'grab',
                    pointerEvents: 'auto'
                }}
                className="relative group"
            >
                {/* Glow Backdrop */}
                <div className={`absolute inset-0 rounded-2xl opacity-60 blur-md transition-all duration-500 group-hover:opacity-100 ${isFuture
                    ? 'bg-gradient-to-r from-cyber-cyan via-cyber-purple to-cyber-pink shadow-[0_0_30px_rgba(0,240,255,0.5)]'
                    : 'bg-gradient-to-r from-cyber-pink to-cyber-purple shadow-[0_0_30px_rgba(255,0,122,0.5)]'
                    }`} />

                {/* 3D Beveled Card Face */}
                <div className="relative flex items-center gap-3.5 px-5 py-3.5 bg-[#0a0a16]/95 backdrop-blur-xl border border-white/10 rounded-md shadow-[inset_0_2px_2px_rgba(255,255,255,0.15),0_15px_35px_rgba(0,0,0,0.95),0_5px_15px_rgba(0,0,0,0.5)] transform transition-transform duration-500 group-hover:scale-[1.02]">

                    {/* 3D Corner Decors */}
                    <div className="absolute top-0 left-0 w-2 h-2 border-t-2 border-l-2 border-cyber-cyan/50 rounded-tl" />
                    <div className="absolute top-0 right-0 w-2 h-2 border-t-2 border-r-2 border-cyber-cyan/50 rounded-tr" />
                    <div className="absolute bottom-0 left-0 w-2 h-2 border-b-2 border-l-2 border-cyber-cyan/50 rounded-bl" />
                    <div className="absolute bottom-0 right-0 w-2 h-2 border-b-2 border-r-2 border-cyber-cyan/50 rounded-br" />

                    {/* Countdown / Text */}
                    <div className="text-center w-56">
                        {isFuture ? (
                            <>
                                <p className="text-[11px] font-cyber font-bold text-center justify-center tracking-[0.25em] text-cyber-cyan flex items-center gap-1 uppercase">
                                    <Sparkles size={12} className="animate-pulse text-cyber-pink" /> Purchase Feature <Sparkles size={12} className="animate-pulse text-cyber-pink rotate-90" />
                                </p>
                                {/* <p className="text-[11px] font-cyber font-bold text-center justify-center tracking-[0.25em] text-cyber-cyan flex items-center gap-1 uppercase">
                                       <Sparkles size={12} className="animate-pulse text-cyber-pink" /> Game Purchase <Sparkles size={12} className="animate-pulse text-cyber-pink rotate-90" />
                                    </p> */}
                                <h4 className="text-sm sm:text-lg font-cyber font-black tracking-widest text-white mt-1 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                                    {timeLeft.days > 0 ? (
                                        <>{padZero(timeLeft.days)}d : {padZero(timeLeft.hours)}h : {padZero(timeLeft.minutes)}m</>
                                    ) : (
                                        <>{padZero(timeLeft.hours)}h : {padZero(timeLeft.minutes)}m : {padZero(timeLeft.seconds)}s</>
                                    )}
                                </h4>
                            </>
                        ) : (
                            <>
                                {/* <p className="text-[9px] font-cyber font-bold justify-center tracking-[0.25em] text-cyber-pink flex items-center gap-1 uppercase">
                                        <Lock size={12} className='text-cyber-cyan animate-pulse' /> Purchases Start  <Lock size={12} className='text-cyber-cyan animate-pulse' />
                                    </p> */}
                                <p className="text-[11px] font-cyber font-bold text-center justify-center tracking-[0.25em] text-cyber-cyan flex items-center gap-1 uppercase">
                                    <Sparkles size={12} className="animate-pulse text-cyber-pink" /> Purchase Feature <Sparkles size={12} className="animate-pulse text-cyber-pink rotate-90" />
                                </p>
                                {/* <p className="text-[11px] font-cyber font-bold text-center justify-center tracking-[0.25em] text-cyber-cyan flex items-center gap-1 uppercase">
                                        <Sparkles size={12} className="animate-pulse text-cyber-pink" /> Game Purchase <Sparkles size={12} className="animate-pulse text-cyber-pink rotate-90" />
                                    </p> */}
                                <h4 className="text-sm sm:text-lg font-cyber text-cyber-cyan animate-pulse font-black tracking-widest text-transparent bg-clip-text bg-gradient-to-r from-cyber-pink via-cyber-purple to-cyber-pink mt-1 uppercase drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                                    Coming Soon
                                </h4>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
